/**
 * The prompt box's frame: the glass shell that paints its surface, the host
 * that draws its outline, radius and shadow, and the slot for the context
 * strip that hangs under it (the chat's space). The chat view and Home both
 * frame ChatComposer with this one component, so the two prompt boxes cannot
 * drift apart.
 *
 * @module components/chat/ChatComposerFrame
 */
import type { ComponentProps, CSSProperties, ReactNode, Ref } from "react";

import { cn } from "~/lib/utils";
import { ComposerFinishContext } from "./composerFinish";

export interface ChatComposerFrameProps extends Omit<ComponentProps<"div">, "ref"> {
  /** The composer itself (ChatComposer). */
  readonly children: ReactNode;
  /**
   * The strip under the composer (ComposerSpaceStrip). The shell's glass stops
   * where a rendered strip begins (index.css), so one that renders nothing
   * leaves the box whole.
   */
  readonly contextStrip?: ReactNode;
  /** A drawer is attached above the composer: the glass splits into exact surfaces. */
  readonly attached?: boolean;
  /** Ref on the box that directly wraps the composer. */
  readonly composerAnchorRef?: Ref<HTMLDivElement> | undefined;
}

/** The page's hairline at 70%, as Home's cards draw theirs. */
const OUTLINE = { "--chat-composer-outline": "color-mix(in oklab, var(--border) 70%, transparent)" } as CSSProperties;

export function ChatComposerFrame({
  children,
  contextStrip = null,
  attached = false,
  composerAnchorRef,
  className,
  style,
  ...props
}: ChatComposerFrameProps) {
  return (
    <ComposerFinishContext value="fine">
      <div
        {...props}
        style={{ ...OUTLINE, ...style }}
        className={cn(
          "chat-composer-glass-shell relative mx-auto w-full max-w-3xl",
          attached && "chat-composer-glass-shell-attached",
          contextStrip !== null && "chat-composer-glass-shell-with-context",
          className,
        )}
      >
        <div className="chat-composer-glass-host relative z-10 w-full rounded-[16px]">
          <div ref={composerAnchorRef} className="relative z-10">
            {children}
          </div>
        </div>
        {contextStrip !== null ? <div className="pointer-events-auto relative z-0">{contextStrip}</div> : null}
      </div>
    </ComposerFinishContext>
  );
}
